import { useCallback, useEffect, useRef, useState } from 'react';
import type { PdvCardapioItem, PdvCardapioItemMarcacao, PdvConfigEmpresa, PdvSyncStatus } from '@/shared/types';
import { midiasService } from '@/shared/services/midiasService';
import { pdvEngajamentoRepository } from '../../data/pdvEngajamentoRepository';
import type { PdvRegras } from '../../domain/ports/IPdvEngajamentoRepository';

export function usePdvAdmin(empresaId: string) {
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState<PdvConfigEmpresa | null>(null);
  const [status, setStatus] = useState<PdvSyncStatus | null>(null);
  const [itens, setItens] = useState<PdvCardapioItem[]>([]);
  const [editando, setEditando] = useState<PdvCardapioItem | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [sincronizando, setSincronizando] = useState(false);
  const [erroMsg, setErroMsg] = useState<string | null>(null);
  const recarregarTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => { if (recarregarTimerRef.current) clearTimeout(recarregarTimerRef.current); }, []);

  const recarregar = useCallback(async () => {
    setLoading(true); setErroMsg(null);
    try {
      const cfg = await pdvEngajamentoRepository.config(empresaId);
      setConfig(cfg);
      if (cfg) {
        const [statusData, itensData] = await Promise.all([
          pdvEngajamentoRepository.status(empresaId),
          pdvEngajamentoRepository.listarCardapio(empresaId),
        ]);
        setStatus(statusData); setItens(itensData);
      }
    } catch (e) { setErroMsg((e as Error).message); }
    finally { setLoading(false); }
  }, [empresaId]);

  useEffect(() => { void recarregar(); }, [recarregar]);

  const salvarItem = useCallback(async (marcacao: PdvCardapioItemMarcacao) => {
    if (!editando) return;
    setSalvando(true); setErroMsg(null);
    try {
      await pdvEngajamentoRepository.marcarItem(empresaId, editando.id, marcacao);
      setEditando(null); setItens(await pdvEngajamentoRepository.listarCardapio(empresaId));
    } catch (e) { setErroMsg((e as Error).message); }
    finally { setSalvando(false); }
  }, [editando, empresaId]);

  const salvarRegras = useCallback(async (regras: PdvRegras) => {
    setSalvando(true); setErroMsg(null);
    try { await pdvEngajamentoRepository.salvarRegras(empresaId, regras); await recarregar(); }
    catch (e) { setErroMsg((e as Error).message); }
    finally { setSalvando(false); }
  }, [empresaId, recarregar]);

  const sincronizar = useCallback(async () => {
    setSincronizando(true); setErroMsg(null);
    try {
      await pdvEngajamentoRepository.sincronizarCardapio(empresaId);
      // A Edge Function roda assíncrona via pg_net — recarrega depois de alguns segundos.
      recarregarTimerRef.current = setTimeout(() => { void recarregar(); }, 5_000);
    }
    catch (e) { setErroMsg((e as Error).message); }
    finally { setSincronizando(false); }
  }, [empresaId, recarregar]);

  const enviarImagemItem = useCallback(async (file: File): Promise<string> => {
    return midiasService.upload(empresaId, file, () => {});
  }, [empresaId]);

  return {
    loading, config, status, itens, editando, salvando, sincronizando, erroMsg,
    abrirEditar: setEditando, fecharModal: () => setEditando(null), salvarItem, salvarRegras, sincronizar, recarregar,
    enviarImagemItem,
  };
}
